(() => {
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class EditorialGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-ep-gallery-track]');
      this.slides = Array.from(this.querySelectorAll('[data-ep-gallery-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-ep-gallery-thumb]'));
      this.thumbTrack = this.querySelector('[data-ep-gallery-thumbs]');
      this.counter = this.querySelector('[data-ep-gallery-current]');
      // The step buttons beside the thumbnails and the desktop arrows on the main image share these attributes.
      this.previousButtons = Array.from(this.querySelectorAll('[data-ep-gallery-prev]'));
      this.nextButtons = Array.from(this.querySelectorAll('[data-ep-gallery-next]'));
      if (!this.track || this.slides.length === 0) return;

      this.index = 0;
      this.previousButtons.forEach((button) => button.addEventListener('click', () => this.go(this.index - 1)));
      this.nextButtons.forEach((button) => button.addEventListener('click', () => this.go(this.index + 1)));
      this.thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => this.go(index)));
      this.track.addEventListener('scroll', () => this.onScroll(), { passive: true });
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
        // The gallery scrolls itself; the browser's own arrow-key scrolling would stop the strip between thumbnails.
        event.preventDefault();
        this.go(this.index + (event.key === 'ArrowLeft' ? -1 : 1));
        if (this.thumbTrack?.contains(document.activeElement)) this.thumbs[this.index].focus({ preventScroll: true });
      });
      // Neighbouring images load once the shopper reaches for the gallery, so the next one is ready.
      ['pointerenter', 'touchstart', 'focusin'].forEach((type) => this.addEventListener(type, () => this.preload(), { once: true, passive: true }));
      this.render();
    }

    go(index) {
      const target = clamp(index, 0, this.slides.length - 1);
      // One step slides; a jump from a far thumbnail swaps straight to the image.
      const behavior = Math.abs(target - this.index) > 1 || reducedMotion.matches ? 'auto' : 'smooth';
      this.index = target;
      this.track.scrollTo({ left: target * this.track.clientWidth, behavior });
      this.render();
      this.preload();
    }

    onScroll() {
      window.clearTimeout(this.scrollTimer);
      this.scrollTimer = window.setTimeout(() => {
        const index = clamp(Math.round(this.track.scrollLeft / this.track.clientWidth), 0, this.slides.length - 1);
        if (index === this.index) return;
        this.index = index;
        this.render();
        this.preload();
      }, 60);
    }

    preload() {
      [this.index - 1, this.index + 1].forEach((index) => {
        const image = this.slides[index]?.querySelector('img[loading="lazy"]');
        if (image) image.loading = 'eager';
      });
    }

    // Keeps whole thumbnails in view and the active one off the strip's ends, so its neighbours show too.
    revealThumb() {
      if (!this.thumbTrack || this.thumbs.length < 2) return;
      const step = this.thumbs[1].offsetLeft - this.thumbs[0].offsetLeft;
      if (step <= 0) return;
      const visible = Math.max(1, Math.round((this.thumbTrack.clientWidth + step - this.thumbs[0].offsetWidth) / step));
      let first = Math.round(this.thumbTrack.scrollLeft / step);
      if (this.index <= first) first = this.index - 1;
      else if (this.index >= first + visible - 1) first = this.index - visible + 2;
      first = clamp(first, 0, Math.max(0, this.thumbs.length - visible));
      const left = Math.min(first * step, this.thumbTrack.scrollWidth - this.thumbTrack.clientWidth);
      if (Math.abs(left - this.thumbTrack.scrollLeft) > 1) this.thumbTrack.scrollTo({ left, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }

    render() {
      if (this.counter) this.counter.textContent = String(this.index + 1).padStart(2, '0');
      this.thumbs.forEach((thumb, index) => {
        thumb.toggleAttribute('aria-current', index === this.index);
        thumb.setAttribute('aria-selected', index === this.index ? 'true' : 'false');
      });
      this.previousButtons.forEach((button) => { button.disabled = this.index === 0; });
      this.nextButtons.forEach((button) => { button.disabled = this.index === this.slides.length - 1; });
      this.revealThumb();
    }
  }

  class EditorialCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-ep-carousel-track]');
      this.cards = Array.from(this.querySelectorAll('[data-ep-carousel-card]'));
      if (!this.track || this.cards.length < 2) return;
      this.querySelector('[data-ep-carousel-prev]')?.addEventListener('click', () => this.move(-1));
      this.querySelector('[data-ep-carousel-next]')?.addEventListener('click', () => this.move(1));
    }

    move(direction) {
      const card = this.cards[0];
      const gap = parseFloat(getComputedStyle(this.track).columnGap || 0);
      this.track.scrollBy({ left: direction * (card.getBoundingClientRect().width + gap), behavior: 'smooth' });
    }
  }

  // The mobile purchase bar adds to cart through its own product-form (product-form.js), with the buy box's variant.
  // With data-reveal-from it stays hidden until the top of that section reaches the screen, then stays up for the rest
  // of the page; scrolling back above the section hides it again. A missing section leaves the bar always shown.
  class EditorialStickyPurchase extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        if (!event.target.closest('button[type="submit"]')) return;
        const mainVariant = document.querySelector('.ep-product-shell .product-variant-id:not([disabled])');
        const stickyVariant = this.querySelector('.product-variant-id');
        if (mainVariant && stickyVariant) stickyVariant.value = mainVariant.value;
      }, true);

      const target = this.dataset.revealFrom && document.getElementById(this.dataset.revealFrom);
      if (!target) {
        this.removeAttribute('data-hidden');
        return;
      }
      const update = () => {
        this.frame = 0;
        this.toggleAttribute('data-hidden', target.getBoundingClientRect().top >= window.innerHeight);
      };
      const schedule = () => {
        if (!this.frame) this.frame = window.requestAnimationFrame(update);
      };
      this.listeners = new AbortController();
      window.addEventListener('scroll', schedule, { passive: true, signal: this.listeners.signal });
      window.addEventListener('resize', schedule, { passive: true, signal: this.listeners.signal });
      update();
    }

    disconnectedCallback() {
      this.listeners?.abort();
      window.cancelAnimationFrame(this.frame);
      this.frame = 0;
    }
  }

  class EditorialFeatureExplorer extends HTMLElement {
    connectedCallback() {
      this.querySelectorAll('[data-ep-modal-open]').forEach((trigger) => {
        trigger.addEventListener('click', () => this.open(trigger.dataset.epModalOpen));
        trigger.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          this.open(trigger.dataset.epModalOpen);
        });
      });

      this.querySelectorAll('.ep-feature-modal').forEach((dialog) => {
        dialog.querySelector('[data-ep-modal-close]')?.addEventListener('click', () => dialog.close());
        dialog.addEventListener('click', (event) => {
          if (event.target === dialog) dialog.close();
        });
        dialog.addEventListener('close', () => document.documentElement.classList.remove('ep-modal-open'));
      });
    }

    open(id) {
      const dialog = this.querySelector(`#${CSS.escape(id)}`);
      if (!dialog) return;
      document.documentElement.classList.add('ep-modal-open');
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      dialog.querySelector('[data-ep-modal-close]')?.focus();
    }
  }

  class EditorialFaqTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[data-ep-faq-tab]'));
      this.items = Array.from(this.querySelectorAll('[data-ep-faq-item]'));
      if (!this.tabs.length || !this.items.length) return;

      const categories = new Set(this.items.map((item) => item.dataset.epFaqCategory));
      this.tabs.forEach((tab) => {
        tab.hidden = !categories.has(tab.dataset.epFaqTab);
        tab.addEventListener('click', () => this.show(tab.dataset.epFaqTab));
      });

      const initial = categories.has('top') ? 'top' : this.items[0].dataset.epFaqCategory;
      this.show(initial);
    }

    show(category) {
      this.tabs.forEach((tab) => tab.setAttribute('aria-pressed', String(tab.dataset.epFaqTab === category)));
      let opened = false;
      this.items.forEach((item) => {
        const visible = item.dataset.epFaqCategory === category;
        item.hidden = !visible;
        if (!visible) item.open = false;
        if (visible && !opened && this.hasAttribute('data-open-first')) {
          item.open = true;
          opened = true;
        }
      });
    }
  }

  if (!customElements.get('ep-gallery')) customElements.define('ep-gallery', EditorialGallery);
  if (!customElements.get('ep-carousel')) customElements.define('ep-carousel', EditorialCarousel);
  if (!customElements.get('ep-sticky-purchase')) customElements.define('ep-sticky-purchase', EditorialStickyPurchase);
  if (!customElements.get('ep-feature-explorer')) customElements.define('ep-feature-explorer', EditorialFeatureExplorer);
  if (!customElements.get('ep-faq-tabs')) customElements.define('ep-faq-tabs', EditorialFaqTabs);
})();
